import L from 'leaflet';
import { createIllustratedMapStyle, MAP_ATTRIBUTION } from './illustratedMapStyle';

// A map can disappear while the renderer chunk is loading (closing a modal or
// returning to List View). Never attach a late renderer to a removed map.
export function addIllustratedBasemap(map) {
  let disposed=false, layer, gl, timeout, fallback=false;
  const container=map.getContainer();
  container.classList.add('illustrated-basemap');
  container.dataset.basemap='loading';
  const showFallback=() => {
    if(disposed || fallback) return;
    fallback=true;
    clearTimeout(timeout);
    if(layer) {
      // An unsupported GPU can fail inside the adapter's onAdd, before it has
      // a GL map to remove. Still clean up the Leaflet layer and its container.
      if(!layer.getMaplibreMap()) layer.onRemove=()=>layer.getContainer()?.remove();
      map.removeLayer(layer);layer=null;
    }
    map.attributionControl?.removeAttribution(MAP_ATTRIBUTION);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxNativeZoom:19,maxZoom:20
    }).addTo(map);
    container.dataset.basemap='fallback';
  };
  const dispose=() => {
    disposed=true;
    clearTimeout(timeout);
    // Leaflet removes its layers as part of map.remove().
    gl?.off('error',onError);
    gl?.off('load',onLoad);
    gl?.off('sourcedata',onSourceData);
  };
  const onError=event => {
    // A missing individual tile/glyph should not discard a working map.
    // Startup timeout handles a failed source; context loss handles GPU failure.
    if(event.error?.message?.includes('WebGL')) showFallback();
  };
  const onLoad=() => {
    if(disposed || fallback) return;
    clearTimeout(timeout);
    container.dataset.basemap='illustrated';
  };
  const onSourceData=event => {
    // The provider may finish some visible tiles before others. Keep a map
    // that is already drawing, instead of discarding it for one slow tile.
    if(event.sourceId==='neighborhood' && event.tile && event.coord) onLoad();
  };
  map.on('unload',dispose);
  timeout=setTimeout(showFallback,15000);
  import('./illustratedMapRenderer').then(({maplibreGL,addIllustratedDestinations}) => {
    if(disposed || fallback) return;
    try {
      layer=maplibreGL({
        style:createIllustratedMapStyle(),interactive:false,
        attributionControl:{customAttribution:MAP_ATTRIBUTION},
        pixelRatio:Math.min(window.devicePixelRatio || 1,2),
        renderWorldCopies:true,padding:0.1,
        canvasContextAttributes:{antialias:false},
        cancelPendingTileRequestsWhileZooming:true
      });
      layer.addTo(map);
      gl=layer.getMaplibreMap();
      gl.once('style.load',()=>{
        addIllustratedDestinations(gl,()=>!disposed && !fallback).then(()=>{
          if(!disposed && !fallback) container.dataset.destinations='ready';
        }).catch(()=>{});
      });
      gl.on('error',onError);
      gl.on('load',onLoad);
      gl.on('sourcedata',onSourceData);
      gl.getCanvas().addEventListener('webglcontextlost',showFallback,{once:true});
    } catch {
      showFallback();
    }
  }).catch(showFallback);
  return dispose;
}
